
import { Injectable, Query, QueryList } from '@angular/core';
import { decrypt, encrypt, getValueProperty, memoize, setValueProperty } from '../../helpers/functions.helpers';
import { LinkupService } from '../link-up/linkup.service';
import { LocalStorageService } from './local-storage.service';
import { DaliService, ITrackEventModel, IPageLoadModel } from '@dali/tagger-ng12';
import { GTM_CODE_DEV, GTM_CODE_PROD } from '../../constants/dali.constants';
import { CryptoVaultService } from '../security/crypto-vault.service';
import { HttpHeaders } from '@angular/common/http';
import { IEventValidate, RecaptchaService } from '../security/recaptcha.service';
import { Router } from '@angular/router';
import { Meta } from '@angular/platform-browser';

export enum EApplication {
  DIGITAL_SOLICITATIONS = 'digitalSolicitations',
  BANK = 'bank',
  FIDUCIAL = 'fiducial'
}

export enum EChannelId {
  PortalOperador = '159',
  PortalPublico = '160'
}

@Injectable({
  providedIn: 'root'
})

export class ApplicationService {


  private _channelId: string = '';

  private _application: string = '';

  private _channelName: string = '';

  private _channelsAnalytics: string = '';

  private _context: any = {};

  private _currentApplication: string = '';

  private _isInitAnalytics = false;

  private _isProduction = false;

  private _analyticsUrl = "";

  private _isInitSettings = false;

  private _isInitRecaptcha = false;

  private _defaultApplication: string = "common";

  private _useRecaptcha: boolean = false;

  constructor(
    private _link: LinkupService,
    private _storage: LocalStorageService,
    private _dali: DaliService,
    private readonly _crypto: CryptoVaultService,
    private readonly _recaptcha: RecaptchaService,
    private readonly _router: Router,
    private readonly _metaTagService: Meta
  ) {
    //this.initSettings();
  }

  async initSettings() {
    if (!this._isInitSettings) {
      let contextSettings = await this._link.settings;
      this._defaultApplication = getValueProperty(contextSettings, "commonApplication", "common");
      this._useRecaptcha = getValueProperty(contextSettings, 'recaptcha.enabled', false);
      this._isProduction = getValueProperty(contextSettings, 'analytics.production', false);
      this._analyticsUrl = getValueProperty(contextSettings, 'analytics.analyticsDataURL', "./assets/analytics/analytics-data.json");
      this._channelsAnalytics = getValueProperty(contextSettings, 'analytics.channels', ["160"]);
      this._context = getValueProperty(contextSettings, 'context.settings');
      let defaultApplication = getValueProperty(contextSettings, 'context.defaultApplication', false);
      this._application = getValueProperty(contextSettings, `context.channelConfig.app`, 'PP');
      this._channelName = getValueProperty(contextSettings, `context.channelConfig.channel_name`, 'Fiducial');
      this._channelId = getValueProperty(contextSettings, `context.channelConfig.channel_id`, '160');
      this._currentApplication = this._storage.getItem("appCurrentApplication") || defaultApplication;
      await this.registerMetaTags(getValueProperty(contextSettings, 'metaTags', []));
    }
  }

  /**
   * Ajustes correspondientes PPG327PP-7216 Adición meta tags para carga de script recaptcha
   * @param metaTags 
   */
  async registerMetaTags(metaTags: Array<any>) {
    this._metaTagService.addTags(metaTags);
  }

  public get channelId(): string {
    return this._channelId;
  }
  public set channelId(value: string) {
    this._channelId = value;
  }

  public get application(): string {
    return this._application;
  }
  public set application(value: string) {
    this._application = value;
  }


  public get channelName(): string {
    return this._channelName;
  }
  public set channelName(value: string) {
    this._channelName = value;
  }

  public get currentApplication(): string {
    return this._currentApplication;
  }

  public get channelsAnalytics(): string {
    return this._channelsAnalytics;
  }

  public set currentApplication(value: string) {
    this._storage.setItem("appCurrentApplication", value);
    this._currentApplication = value;
  }

  public get useRecaptcha(): boolean {
    return this._useRecaptcha;
  }

  public set useRecaptcha(value: boolean) {
    this._useRecaptcha = value;
  }

  isVisible(componentContext: any, section: any) {
    return this.getSetting(componentContext, section, "visible", true);
  }

  getSetting(componentContext: any, section: string, setting: string | null = null, defaultValue: any = null) {
    let application = this.currentApplication;
    let defaultApplication = this._defaultApplication;
    if (setting && setting !== null) {
      let value = getValueProperty(componentContext, `${section}.${application}.${setting}`, null);
      if (value === null) {
        value = getValueProperty(componentContext, `${section}.${defaultApplication}.${setting}`, defaultValue);
      }
      return value;
    }
    else {
      let value = getValueProperty(componentContext, `${section}.${application}`, null);
      if (value === null) {
        value = getValueProperty(componentContext, `${section}.${defaultApplication}`, defaultValue);
      }
      return value;
    }
  }

  async initAnalytics() {
    if (!this._isInitAnalytics && this._analyticsUrl && this.channelsAnalytics.includes(this.channelId)) {
      const GTM_CODE = this._isProduction ? GTM_CODE_PROD : GTM_CODE_DEV;
      this._isInitAnalytics = await this._dali.initAnalytics(GTM_CODE, this._isProduction, this._analyticsUrl);
    }
  }


  async pageLoad(changes: Array<IPageLoadModel>) {
    if (!this._isInitAnalytics) {
      await this.initAnalytics();
    }

    if (this._isInitAnalytics) {
      this._dali.pageLoad(changes);
    }
  }


  async trackEvent(events: ITrackEventModel, changes?: any) {
    if (!this._isInitAnalytics) {
      await this.initAnalytics();
    }
    else if (this._isInitAnalytics) {
      this._dali.trackEvent(events, changes);
    }
  }

  async initCryptoVault(transactionId: string) {
    await this._crypto.keyExchange(transactionId);
  }

  async encrypt(data: any, fields: Array<string>, isHeader = false) {
    return encrypt(this._crypto, data, fields, isHeader);
  }

  async decrypt(data: string) {
    return decrypt(this._crypto, data);
  }

  normaliceCryptoVaultSettings(settings: { fields: any, headers: any, excludeInSettings?: any }) {

    const fields = getValueProperty(settings, "fields", []);
    const headers = getValueProperty(settings, "headers", []);

    let fieldsTemp = [];
    let headersTemp = [];

    for (let field of fields) {
      if (typeof field === 'object') {
        for (let overrideField of Object.keys(field)) {
          fieldsTemp.push(overrideField);
        }
      } else {
        fieldsTemp.push(field);
      }
    }

    for (let header of headers) {
      if (typeof header === 'object') {
        for (let overrideHeader of Object.keys(header)) {
          headersTemp.push(overrideHeader);
        }
      } else {
        headersTemp.push(header);
      }
    }

    return { fields: [...fieldsTemp], headers: [...headersTemp], excludeInSettings: settings.excludeInSettings };
  }

  async initRecaptcha() {
    if (this._useRecaptcha && !this._isInitRecaptcha) {
      await this._recaptcha.register(this._channelId, this._application);
      this._isInitRecaptcha = true;
    }
  }

  async recaptchaToken(options: { action: string }) {
    if (!this._isInitRecaptcha) {
      await this.initRecaptcha();
    }

    if (this._useRecaptcha) {
      return await this._recaptcha.generateToken(options);
    } else {
      return undefined;
    }
  }

  //@memoize()
  async validateRecaptcha(action: string) {
    if (!this._isInitRecaptcha) {
      await this.initRecaptcha();
    }
    if (this._useRecaptcha) {
      let result = await this._recaptcha.isValid(action);
      if (!result) {
        this._router.navigateByUrl('/recaptcha-error', { skipLocationChange: true });
      } else {
        return result;
      }
    } else {
      return true;
    }
  }
}
